import React from 'react';
import { createPlanNameMatcher, getKeyDetailsContentIcon } from './utils/PlanMetaHelper';
import { DETAILS_CONTENT_KEYS, IPlanContent } from '../typings/planContent.types';
import { PlanKeyDetailsModal } from './common/PlanKeyDetailsModal';
import {
  CONTENT_SEPARATOR,
  PLAN_DETAILS_PLAN_PERIOD,
  PLAN_DETAILS_PLAN_PERIOD_BENEFIT,
} from '../typings';
import {
  defaultEstimatesDialog,
  businessDualKeyDetailsAddons,
  businessElectricityKeyDetailsAddons,
} from './common/planDefaults';
import { List, ListItem, Stack, Text, TextLink } from '@origin-digital/ods-core';
import { BYOBattery } from './common/BYOBatteryModal';

const PLAN_TONE = 'promoteB';

const solarInfoContent = (
  <Stack space="small">
    <Text>With Origin Business Solar Boost (with tiered feed-in tariff):</Text>
    <List space="small">
      <ListItem>your solar PV system must be net metered, and</ListItem>
      <ListItem>
        you must not be receiving a feed-in tariff (FIT) under any government scheme.
      </ListItem>
    </List>
    <Text>
      You’ll receive a higher than standard FIT until the daily export limit of 8kWh is reached. The
      daily export limit is averaged across your billing period (by multiplying the number of days
      in your billing period by the daily export limit). You’ll receive our standard retailer FIT
      (as published on our website at the time) if you export more than the daily limit and once
      your energy plan ends. FIT rates are GST inclusive (where applicable). For Victorian
      customers, the{' '}
      <TextLink
        href="https://www.originenergy.com.au/terms-and-conditions/vic-feed-in-tariff/"
        target="_blank"
      >
        Terms and Conditions for Feed-in Tariffs - Victoria
      </TextLink>
       apply to both FITs.
    </Text>
  </Stack>
);

export const originBusinessSolarBoost: IPlanContent = {
  test: createPlanNameMatcher(['Origin Business Solar Boost']),
  content: {
    rateChangePrepositionText: {
      VIC: {
        defaultFuel:
          "Our electricity rates will change on 1 August. You'll see your new charges on your next bill after that time.",
        gas: '',
      },
      defaultState: {
        defaultFuel: '',
      },
    },
    rateDiscountText: {
      defaultState: {
        defaultFuel: 'Rates variable on a 12 month benefit period',
      },
    },
    keyFeatures: {
      defaultState: {
        defaultFuel: ['Benefit period is 12 months', 'No lock in contract or exit fees'],
      },
    },
    keyDetails: {
      defaultState: {
        defaultFuel: [
          {
            contentKey: DETAILS_CONTENT_KEYS.Rates,
            contentValue: 'Variable',
            modalContent: PlanKeyDetailsModal,
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.Rates),
            contentDescription: 'Variable rates',
          },
          {
            contentKey: DETAILS_CONTENT_KEYS.ExitFees,
            contentValue: 'No',
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.ExitFees),
            contentDescription: 'No exit fees. Leave anytime.',
          },
          {
            contentKey: DETAILS_CONTENT_KEYS.BenefitPeriod,
            contentValue:
              PLAN_DETAILS_PLAN_PERIOD.Months12 +
              CONTENT_SEPARATOR +
              PLAN_DETAILS_PLAN_PERIOD_BENEFIT,
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BenefitPeriod),
            contentDescription: '12 month benefit period',
          },
        ],
      },
    },
    keyDetailsAddons: {
      defaultState: {
        defaultFuel: [],
        dual: [
          ...businessDualKeyDetailsAddons,
          {
            contentKey: DETAILS_CONTENT_KEYS.BYOBattery,
            contentValue: 'Register',
            modalContent: BYOBattery,
            // for plan card redesign in sales-monorepo only
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BYOBattery),
            contentDescription: 'BYO battery for extra value',
          },
        ],
        electricity: [
          ...businessElectricityKeyDetailsAddons,
          {
            contentKey: DETAILS_CONTENT_KEYS.BYOBattery,
            contentValue: 'Register',
            modalContent: BYOBattery,
            // for plan card redesign in sales-monorepo only
            contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BYOBattery),
            contentDescription: 'BYO battery for extra value',
          },
        ],
      },
    },
    uiOptions: {
      defaultState: {
        defaultFuel: {
          planColor: {
            text: {
              styleGuide: 'purple',
              odsCore: PLAN_TONE,
            },
            promobox: {
              styleGuide: 'purple',
              odsCore: PLAN_TONE,
            },
            bannerText: {
              styleGuide: 'white',
              odsCore: 'white',
            },
          },
          imageUrl:
            'https://res.cloudinary.com/originenergy/image/upload/v1728624900/spot-illustrations/spot_Origin-Solar-Boost.svg',
          imageForFuelType: {
            electricity:
              'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/CP/plans/cp_origin-solar-boost.svg',
            dual: 'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/CP/plans/cp_origin-solar-boost.svg',
          },
        },
      },
    },
    tagLine: {
      defaultState: {
        defaultFuel: 'A boosted feed-in tariff on a 12 month energy plan.',
      },
    },
    termsAndConditionsUrl: {
      defaultState: {
        defaultFuel: '/terms-and-conditions.html',
      },
    },
    estimatesDialog: defaultEstimatesDialog({ customerType: 'business' }),
    solarInfo: {
      defaultState: {
        defaultFuel: {
          title: 'Origin Business Solar Boost',
          content: solarInfoContent,
        },
      },
    },
  },
};
